import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import ticketService from '../services/ticketService';
import schoolsService from '../services/schoolsService';
import campusService from '../services/campusService';
import {
  Buildings, Users, Student, Users as StaffIcon, ActivityIcon,
  SignOut, User, Gear, Bell, CheckCircle, WarningCircle, XCircle,
  Plus, Eye, PencilSimple, Trash, CaretRight, MagnifyingGlass, X,
  Ticket as TicketIcon, PaperPlaneTilt, Clock, CurrencyCircleDollar
} from '@phosphor-icons/react';
import './SuperAdminPortal.css';

function SuperAdminPortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [newSchool, setNewSchool] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    region: '',
    school_type: 'basic'
  });
  // School view/edit states
  const [showSchoolDetailModal, setShowSchoolDetailModal] = useState(false);
  const [showEditSchoolModal, setShowEditSchoolModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  // Ticket states
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);
  const [ticketFilterStatus, setTicketFilterStatus] = useState(null);
  const [ticketFilterCategory, setTicketFilterCategory] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);

  // Create user account modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserTab, setCreateUserTab] = useState('single');
  const [newUserForm, setNewUserForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'school_admin', school_id: '', campus_id: '' });
  const [newUserCampuses, setNewUserCampuses] = useState([]);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  useEffect(() => {
    if (!newUserForm.school_id) {
      setNewUserCampuses([]);
      return;
    }
    campusService.listCampuses(newUserForm.school_id)
      .then(data => setNewUserCampuses(Array.isArray(data) ? data : []))
      .catch(() => setNewUserCampuses([]));
  }, [newUserForm.school_id]);

  // School direct-settlement payout verification
  const [pendingSchoolPayouts, setPendingSchoolPayouts] = useState([]);
  const [busySchoolPayoutId, setBusySchoolPayoutId] = useState(null);
  // Canteen direct-settlement payout verification (independent subaccount)
  const [pendingCanteenPayouts, setPendingCanteenPayouts] = useState([]);
  const [busyCanteenPayoutId, setBusyCanteenPayoutId] = useState(null);

  // Check if user has required role
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardData, schoolsData] = await Promise.all([
        api.get('/api/dashboard/overview').catch(() => null),
        schoolsService.listSchools().catch(() => [])
      ]);

      setProfile(user);
      setStats(dashboardData);
      setSchools(Array.isArray(schoolsData) ? schoolsData : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadTickets = useCallback(async (page = 1) => {
    try {
      setTicketLoading(true);
      const data = await ticketService.getTickets(page, 20, ticketFilterStatus, ticketFilterCategory);
      setTickets(data.data || []);
      setTicketTotalPages(data.pages || 1);
      setTicketPage(page);
      
      // Load stats
      const statsData = await ticketService.getTicketStats();
      setTicketStats(statsData.data || {});
    } catch (error) {
      toast.error('Failed to load tickets: ' + error.message);
    } finally {
      setTicketLoading(false);
    }
  }, [ticketFilterStatus, ticketFilterCategory]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Load tickets when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'tickets') {
      loadTickets(1);
    }
  }, [activeTab, loadTickets]);

  const loadPendingSchoolPayouts = useCallback(() => {
    schoolsService.listPendingSchoolPayoutDetails()
      .then((data) => setPendingSchoolPayouts(data.schools || []))
      .catch(() => setPendingSchoolPayouts([]));
  }, []);

  const loadPendingCanteenPayouts = useCallback(() => {
    schoolsService.listPendingCanteenPayoutDetails()
      .then((data) => setPendingCanteenPayouts(data.schools || []))
      .catch(() => setPendingCanteenPayouts([]));
  }, []);

  useEffect(() => {
    if (activeTab === 'school-payouts') {
      loadPendingSchoolPayouts();
      loadPendingCanteenPayouts();
    }
  }, [activeTab, loadPendingSchoolPayouts, loadPendingCanteenPayouts]);

  const handleVerifySchoolPayout = async (schoolId) => {
    try {
      setBusySchoolPayoutId(schoolId);
      await schoolsService.verifySchoolPayoutDetails(schoolId);
      toast.success('Verified — a Paystack subaccount was created, fee payments will now settle directly to this school');
      loadPendingSchoolPayouts();
    } catch (err) {
      toast.error(err.message || 'Unable to verify payout details');
    } finally {
      setBusySchoolPayoutId(null);
    }
  };

  const handleRejectSchoolPayout = (schoolId) => {
    setPromptDialog({
      title: 'Reject Payout Details',
      message: 'Reason for rejecting these payout details:',
      defaultValue: '',
      onSubmit: async (reason) => {
        try {
          setBusySchoolPayoutId(schoolId);
          await schoolsService.rejectSchoolPayoutDetails(schoolId, reason);
          toast.success('Rejected — the school can resubmit');
          loadPendingSchoolPayouts();
        } catch (err) {
          toast.error(err.message || 'Unable to reject payout details');
        } finally {
          setBusySchoolPayoutId(null);
        }
      },
    });
  };

  const handleVerifyCanteenPayout = async (schoolId) => {
    try {
      setBusyCanteenPayoutId(schoolId);
      await schoolsService.verifySchoolCanteenPayoutDetails(schoolId);
      toast.success('Verified — a Paystack subaccount was created, canteen top-ups will now settle directly to this school');
      loadPendingCanteenPayouts();
    } catch (err) {
      toast.error(err.message || 'Unable to verify canteen payout details');
    } finally {
      setBusyCanteenPayoutId(null);
    }
  };

  const handleRejectCanteenPayout = (schoolId) => {
    setPromptDialog({
      title: 'Reject Canteen Payout Details',
      message: 'Reason for rejecting these canteen payout details:',
      defaultValue: '',
      onSubmit: async (reason) => {
        try {
          setBusyCanteenPayoutId(schoolId);
          await schoolsService.rejectSchoolCanteenPayoutDetails(schoolId, reason);
          toast.success('Rejected — the school can resubmit');
          loadPendingCanteenPayouts();
        } catch (err) {
          toast.error(err.message || 'Unable to reject canteen payout details');
        } finally {
          setBusyCanteenPayoutId(null);
        }
      },
    });
  };

  const handleViewTicket = async (ticket) => {
    try {
      const detail = await ticketService.getTicketDetail(ticket.id);
      setSelectedTicket(detail.data);
      setShowTicketDetailModal(true);
    } catch (error) {
      toast.error('Failed to load ticket details: ' + error.message);
    }
  };

  const handleAddTicketComment = async () => {
    if (!selectedTicket || !newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      await ticketService.addComment(selectedTicket.id, newComment, false);
      toast.success('Comment added!');
      setNewComment('');
      // Reload ticket detail
      const detail = await ticketService.getTicketDetail(selectedTicket.id);
      setSelectedTicket(detail.data);
    } catch (error) {
      toast.error('Failed to add comment: ' + error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;

    try {
      await ticketService.updateTicketStatus(selectedTicket.id, newStatus);
      toast.success('Ticket status updated!');
      setShowTicketDetailModal(false);
      loadTickets(ticketPage);
    } catch (error) {
      toast.error('Failed to update status: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#3b82f6',
      medium: '#f59e0b',
      high: '#ef4444',
      urgent: '#dc2626'
    };
    return colors[priority] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#6b7280',
      in_progress: '#3b82f6',
      resolved: '#10b981',
      closed: '#10b981'
    };
    return colors[status] || '#6b7280';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateSchool = async () => {
    if (!newSchool.name || !newSchool.code) {
      toast.error('School name and code are required');
      return;
    }

    try {
      const response = await schoolsService.createSchool(newSchool);
      toast.success('School created successfully');
      setSchools([...schools, response]);
      setShowSchoolModal(false);
      setNewSchool({
        name: '',
        code: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        region: '',
        school_type: 'basic'
      });
    } catch (err) {
      toast.error(err.detail || 'Failed to create school');
    }
  };

  const handleViewSchool = (school) => {
    setSelectedSchool(school);
    setShowSchoolDetailModal(true);
  };

  const handleEditSchool = (school) => {
    setEditingSchool({ ...school });
    setShowEditSchoolModal(true);
  };

  const handleUpdateSchool = async () => {
    if (!editingSchool.name || !editingSchool.code) {
      toast.error('School name and code are required');
      return;
    }

    try {
      await schoolsService.updateSchool(editingSchool.id, editingSchool);
      toast.success('School updated successfully');
      setSchools(schools.map(s => s.id === editingSchool.id ? editingSchool : s));
      setShowEditSchoolModal(false);
      setEditingSchool(null);
    } catch (err) {
      toast.error(err.detail || 'Failed to update school');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserForm.first_name || !newUserForm.last_name || !newUserForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    try {
      const payload = { ...newUserForm, campus_id: newUserForm.campus_id || null };
      const res = await api.post('/api/auth/register', payload);
      setCreatedCredentials({ email: res.user.email, password: res.generated_password, name: `${res.user.first_name} ${res.user.last_name}` });
      setNewUserForm({ first_name: '', last_name: '', email: '', phone: '', role: 'school_admin', school_id: '', campus_id: '' });
      setNewUserCampuses([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.detail || 'Failed to create user');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please select a CSV file'); return; }
    setBulkLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await api.post('/api/auth/register/bulk', formData);
      setBulkResult(res);
      setBulkFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,first_name,last_name,role,phone,school_id\njane@school.edu,Jane,Doe,teacher,+233200000000,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'users_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const filteredSchools = schools.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading super admin portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const pageContent = (
    <div className="page super-admin-portal">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Super Admin Portal</h1>
          <p>Manage all schools and system settings</p>
        </div>
        <div className="header-actions">
          <div className="user-info">
            <User size={20} />
            <span>{user?.first_name} {user?.last_name}</span>
          </div>
          <button
            className="btn btn-danger"
            onClick={handleLogout}
            title="Logout"
          >
            <SignOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="portal-tabs">
        <button
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <ActivityIcon size={20} />
          Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'schools' ? 'active' : ''}`}
          onClick={() => setActiveTab('schools')}
        >
          <Buildings size={20} />
          Schools
        </button>
        <button
          className={`tab ${activeTab === 'tickets' ? 'active' : ''}`}
          onClick={() => setActiveTab('tickets')}
        >
          <TicketIcon size={20} />
          Tickets
        </button>
        <button
          className={`tab ${activeTab === 'school-payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('school-payouts')}
        >
          <CurrencyCircleDollar size={20} />
          School Payouts {(pendingSchoolPayouts.length + pendingCanteenPayouts.length) > 0 && `(${pendingSchoolPayouts.length + pendingCanteenPayouts.length})`}
        </button>
        <button
          className="tab"
          onClick={() => navigate('/admin/billing')}
        >
          <CurrencyCircleDollar size={20} />
          Billing Admin
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="tab-content">
          <div className="stats-grid">
            {/* Total Schools */}
            <div className="stat-card">
              <div className="stat-icon schools">
                <Buildings size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Total Schools</h3>
                <p className="stat-number">{stats?.stats?.total_schools || 0}</p>
                <span className="stat-label">Active institutions</span>
              </div>
            </div>

            {/* Total Students */}
            <div className="stat-card">
              <div className="stat-icon students">
                <Student size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Total Students</h3>
                <p className="stat-number">{stats?.stats?.total_students || 0}</p>
                <span className="stat-label">Across all schools</span>
              </div>
            </div>

            {/* Total Staff */}
            <div className="stat-card">
              <div className="stat-icon staff">
                <StaffIcon size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Total Staff</h3>
                <p className="stat-number">{stats?.stats?.total_staff || 0}</p>
                <span className="stat-label">Teachers & administrators</span>
              </div>
            </div>

            {/* System Status */}
            <div className="stat-card">
              <div className="stat-icon status">
                <ActivityIcon size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>System Status</h3>
                <p className="stat-number healthy">
                  <CheckCircle size={20} />
                  Operational
                </p>
                <span className="stat-label">All systems normal</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="section">
            <h2>Quick Actions</h2>
            <div className="actions-grid">
              <button className="action-card" onClick={() => setActiveTab('schools')}>
                <Buildings size={28} />
                <h3>Manage Schools</h3>
                <p>Create, edit, and monitor schools</p>
              </button>
              <button className="action-card" onClick={() => navigate('/tickets')}>
                <Bell size={28} />
                <h3>View Tickets</h3>
                <p>Support tickets from schools</p>
              </button>
              <button className="action-card" onClick={() => navigate('/sms-dashboard')}>
                <ActivityIcon size={28} />
                <h3>SMS Dashboard</h3>
                <p>Manage SMS communications</p>
              </button>
              <button className="action-card" onClick={() => setActiveTab('settings')}>
                <Gear size={28} />
                <h3>System Settings</h3>
                <p>Configure system-wide settings</p>
              </button>
            </div>
          </div>

          {/* Recent Schools */}
          <div className="section">
            <h2>Recent Schools</h2>
            <div className="schools-list">
              {schools.slice(0, 5).map((school) => (
                <div key={school.id} className="school-item">
                  <div className="school-info">
                    <h3>{school.name}</h3>
                    <p>{school.code} • {school.city}</p>
                  </div>
                  <button className="btn btn-small" onClick={() => setSelectedSchool(school)}>
                    <Eye size={16} />
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schools Tab */}
      {activeTab === 'schools' && (
        <div className="tab-content">
          <div className="section-header">
            <div className="search-bar">
              <MagnifyingGlass size={20} />
              <input
                type="text"
                placeholder="Search schools by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setCreatedCredentials(null); setShowCreateUserModal(true); }}
              >
                <Users size={18} />
                Create Account
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowSchoolModal(true)}
              >
                <Plus size={18} />
                Create School
              </button>
            </div>
          </div>

          {/* Schools Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>School Name</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchools.map((school) => (
                  <tr key={school.id}>
                    <td className="font-medium">{school.name}</td>
                    <td>{school.code}</td>
                    <td>
                      <span className="badge badge-primary">
                        {school.school_type}
                      </span>
                    </td>
                    <td>{school.city}, {school.region}</td>
                    <td>{school.phone}</td>
                    <td>
                      <div className="actions">
                        <button
                          className="icon-btn"
                          onClick={() => handleViewSchool(school)}
                          title="View details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => handleEditSchool(school)}
                          title="Edit school"
                        >
                          <PencilSimple size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="tab-content">
          {/* Ticket Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon schools">
                <Clock size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Open Tickets</h3>
                <p className="stat-number">{ticketStats.open || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon students">
                <ActivityIcon size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>In Progress</h3>
                <p className="stat-number">{ticketStats.in_progress || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon staff">
                <CheckCircle size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Resolved</h3>
                <p className="stat-number">{ticketStats.resolved || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon status">
                <CheckCircle size={32} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>Closed</h3>
                <p className="stat-number">{ticketStats.closed || 0}</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="section">
            <div className="section-header">
              <div className="filters">
                <select
                  value={ticketFilterStatus || ''}
                  onChange={(e) => {
                    setTicketFilterStatus(e.target.value || null);
                    setTicketPage(1);
                  }}
                  className="filter-select"
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={ticketFilterCategory || ''}
                  onChange={(e) => {
                    setTicketFilterCategory(e.target.value || null);
                    setTicketPage(1);
                  }}
                  className="filter-select"
                >
                  <option value="">All Categories</option>
                  <option value="technical">Technical</option>
                  <option value="academic">Academic</option>
                  <option value="finance">Finance</option>
                  <option value="hr">HR</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Tickets Table */}
            {ticketLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="info-box">
                <WarningCircle size={20} />
                <p>No tickets found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Created By</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="font-medium">#{ticket.id.slice(0, 8)}</td>
                          <td>{ticket.title}</td>
                          <td>
                            <span className="badge badge-secondary">
                              {ticket.category}
                            </span>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: getPriorityColor(ticket.priority),
                                color: 'white'
                              }}
                            >
                              {ticket.priority}
                            </span>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: getStatusColor(ticket.status),
                                color: 'white'
                              }}
                            >
                              {ticket.status}
                            </span>
                          </td>
                          <td>{ticket.created_by_name || 'Unknown'}</td>
                          <td>
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <button
                              className="icon-btn"
                              onClick={() => handleViewTicket(ticket)}
                              title="View ticket details"
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {ticketTotalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => loadTickets(ticketPage - 1)}
                      disabled={ticketPage === 1}
                      className="btn btn-secondary btn-small"
                    >
                      Previous
                    </button>
                    <span className="page-info">
                      Page {ticketPage} of {ticketTotalPages}
                    </span>
                    <button
                      onClick={() => loadTickets(ticketPage + 1)}
                      disabled={ticketPage === ticketTotalPages}
                      className="btn btn-secondary btn-small"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* School Payouts Tab */}
      {activeTab === 'school-payouts' && (
        <div className="tab-content" style={{ display: 'grid', gap: 28 }}>
          <div>
            <div className="section-header">
              <div>
                <h2 style={{ margin: 0 }}>Fee Payout — Pending Verification</h2>
                <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
                  Verifying creates a real Paystack subaccount — fee payments then settle straight to the
                  school's own bank/MoMo account automatically, with no withdrawal step.
                </p>
              </div>
            </div>

            {pendingSchoolPayouts.length === 0 ? (
              <div className="table-container" style={{ padding: 20, color: '#6b7280' }}>
                Nothing waiting for verification.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {pendingSchoolPayouts.map((s) => (
                  <div key={s.id} className="table-container" style={{ padding: 16 }}>
                    <div style={{ fontWeight: 700 }}>{s.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{s.code}</div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
                      <strong>{s.payout_account_name}</strong> — {(s.payout_account_type || '').replace('_', ' ')} · {s.payout_account_number}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                      Name resolved directly from the bank/mobile money provider — not typed in by the school.
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '6px 14px' }}
                        disabled={busySchoolPayoutId === s.id}
                        onClick={() => handleVerifySchoolPayout(s.id)}
                      >
                        <CheckCircle size={14} /> Verify
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 14px' }}
                        disabled={busySchoolPayoutId === s.id}
                        onClick={() => handleRejectSchoolPayout(s.id)}
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="section-header">
              <div>
                <h2 style={{ margin: 0 }}>Canteen Payout — Pending Verification</h2>
                <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
                  A separate subaccount from fee payments, since a school's canteen is often run against its
                  own bank/MoMo account. Verifying settles canteen top-ups there automatically.
                </p>
              </div>
            </div>

            {pendingCanteenPayouts.length === 0 ? (
              <div className="table-container" style={{ padding: 20, color: '#6b7280' }}>
                Nothing waiting for verification.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {pendingCanteenPayouts.map((s) => (
                  <div key={s.id} className="table-container" style={{ padding: 16 }}>
                    <div style={{ fontWeight: 700 }}>{s.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{s.code}</div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
                      <strong>{s.payout_account_name}</strong> — {(s.payout_account_type || '').replace('_', ' ')} · {s.payout_account_number}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                      Name resolved directly from the bank/mobile money provider — not typed in by the school.
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '6px 14px' }}
                        disabled={busyCanteenPayoutId === s.id}
                        onClick={() => handleVerifyCanteenPayout(s.id)}
                      >
                        <CheckCircle size={14} /> Verify
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 14px' }}
                        disabled={busyCanteenPayoutId === s.id}
                        onClick={() => handleRejectCanteenPayout(s.id)}
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketDetailModal && selectedTicket && (
        <div
          className="modal-overlay"
          onClick={() => setShowTicketDetailModal(false)}
        >
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Ticket #{selectedTicket.id.slice(0, 8)}</h2>
                <p>{selectedTicket.title}</p>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowTicketDetailModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-content modal-scrollable">
              {/* Ticket Info */}
              <div className="ticket-info-grid">
                <div>
                  <label>Status</label>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="filter-select"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label>Priority</label>
                  <p className="badge" style={{ background: getPriorityColor(selectedTicket.priority), color: 'white' }}>
                    {selectedTicket.priority}
                  </p>
                </div>
                <div>
                  <label>Category</label>
                  <p className="badge badge-secondary">{selectedTicket.category}</p>
                </div>
                <div>
                  <label>Created Date</label>
                  <p>{new Date(selectedTicket.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Description */}
              <div className="ticket-section">
                <h3>Description</h3>
                <p>{selectedTicket.description}</p>
              </div>

              {/* Comments */}
              <div className="ticket-section">
                <h3>Comments & Updates</h3>
                <div className="comments-list">
                  {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                    selectedTicket.comments.map((comment, idx) => (
                      <div key={idx} className="comment-item">
                        <div className="comment-header">
                          <strong>{comment.author_name || 'Unknown'}</strong>
                          <span className="comment-date">
                            {new Date(comment.created_at).toLocaleDateString()}{' '}
                            {new Date(comment.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p>{comment.comment}</p>
                      </div>
                    ))
                  ) : (
                    <p className="no-comments">No comments yet</p>
                  )}
                </div>

                {/* Add Comment */}
                <div className="add-comment">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows="3"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleAddTicketComment}
                  >
                    <PaperPlaneTilt size={16} />
                    Post Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* School Detail Modal */}
      {showSchoolDetailModal && selectedSchool && (
        <div
          className="modal-overlay"
          onClick={() => setShowSchoolDetailModal(false)}
        >
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>{selectedSchool.name}</h2>
                <p>{selectedSchool.code}</p>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowSchoolDetailModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-content modal-scrollable">
              <div className="ticket-info-grid">
                <div>
                  <label>School Code</label>
                  <p>{selectedSchool.code}</p>
                </div>
                <div>
                  <label>School Type</label>
                  <p className="badge badge-primary">{selectedSchool.school_type}</p>
                </div>
                <div>
                  <label>Status</label>
                  <p className="badge badge-secondary">
                    {selectedSchool.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>

              <div className="ticket-section">
                <h3>Location Information</h3>
                <div className="ticket-info-grid">
                  <div>
                    <label>Address</label>
                    <p>{selectedSchool.address || 'N/A'}</p>
                  </div>
                  <div>
                    <label>City</label>
                    <p>{selectedSchool.city || 'N/A'}</p>
                  </div>
                  <div>
                    <label>Region</label>
                    <p>{selectedSchool.region || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="ticket-section">
                <h3>Contact Information</h3>
                <div className="ticket-info-grid">
                  <div>
                    <label>Email</label>
                    <p>{selectedSchool.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label>Phone</label>
                    <p>{selectedSchool.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {selectedSchool.motto && (
                <div className="ticket-section">
                  <h3>School Motto</h3>
                  <p>{selectedSchool.motto}</p>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSchoolDetailModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowSchoolDetailModal(false);
                  handleEditSchool(selectedSchool);
                }}
              >
                <PencilSimple size={16} />
                Edit School
              </button>
            </div>
          </div>
        </div>
      )}

      {/* School Edit Modal */}
      {showEditSchoolModal && editingSchool && (
        <div
          className="modal-overlay"
          onClick={() => setShowEditSchoolModal(false)}
        >
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Edit School: {editingSchool.name}</h2>
              <button
                className="close-btn"
                onClick={() => setShowEditSchoolModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-content modal-scrollable">
              <div className="form-group">
                <label>School Name *</label>
                <input
                  type="text"
                  value={editingSchool.name}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, name: e.target.value })
                  }
                  placeholder="Enter school name"
                />
              </div>
              <div className="form-group">
                <label>School Code *</label>
                <input
                  type="text"
                  value={editingSchool.code}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, code: e.target.value })
                  }
                  placeholder="Enter school code"
                  disabled
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editingSchool.email}
                    onChange={(e) =>
                      setEditingSchool({ ...editingSchool, email: e.target.value })
                    }
                    placeholder="school@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editingSchool.phone}
                    onChange={(e) =>
                      setEditingSchool({ ...editingSchool, phone: e.target.value })
                    }
                    placeholder="+233 20 123 4567"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={editingSchool.address}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, address: e.target.value })
                  }
                  placeholder="123 Education Street"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={editingSchool.city}
                    onChange={(e) =>
                      setEditingSchool({ ...editingSchool, city: e.target.value })
                    }
                    placeholder="Accra"
                  />
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <input
                    type="text"
                    value={editingSchool.region}
                    onChange={(e) =>
                      setEditingSchool({ ...editingSchool, region: e.target.value })
                    }
                    placeholder="Greater Accra"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>School Type</label>
                <select
                  value={editingSchool.school_type}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, school_type: e.target.value })
                  }
                >
                  <option value="basic">Basic</option>
                  <option value="secondary">Secondary</option>
                  <option value="tertiary">Tertiary</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className="form-group">
                <label>School Motto</label>
                <textarea
                  value={editingSchool.motto || ''}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, motto: e.target.value })
                  }
                  placeholder="Enter school motto"
                  rows="2"
                />
              </div>
              <div className="form-group">
                <label>Active Status</label>
                <select
                  value={editingSchool.is_active ? 'true' : 'false'}
                  onChange={(e) =>
                    setEditingSchool({ ...editingSchool, is_active: e.target.value === 'true' })
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditSchoolModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateSchool}
              >
                <PencilSimple size={16} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create School Modal */}
      {showSchoolModal && (
        <div className="modal-overlay" onClick={() => setShowSchoolModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New School</h2>
              <button
                className="close-btn"
                onClick={() => setShowSchoolModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>School Name *</label>
                <input
                  type="text"
                  value={newSchool.name}
                  onChange={(e) =>
                    setNewSchool({ ...newSchool, name: e.target.value })
                  }
                  placeholder="Enter school name"
                />
              </div>
              <div className="form-group">
                <label>School Code *</label>
                <input
                  type="text"
                  value={newSchool.code}
                  onChange={(e) =>
                    setNewSchool({ ...newSchool, code: e.target.value })
                  }
                  placeholder="Enter school code"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newSchool.email}
                    onChange={(e) =>
                      setNewSchool({ ...newSchool, email: e.target.value })
                    }
                    placeholder="school@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newSchool.phone}
                    onChange={(e) =>
                      setNewSchool({ ...newSchool, phone: e.target.value })
                    }
                    placeholder="+233 20 123 4567"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={newSchool.address}
                  onChange={(e) =>
                    setNewSchool({ ...newSchool, address: e.target.value })
                  }
                  placeholder="123 Education Street"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newSchool.city}
                    onChange={(e) =>
                      setNewSchool({ ...newSchool, city: e.target.value })
                    }
                    placeholder="Accra"
                  />
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <input
                    type="text"
                    value={newSchool.region}
                    onChange={(e) =>
                      setNewSchool({ ...newSchool, region: e.target.value })
                    }
                    placeholder="Greater Accra"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>School Type</label>
                <select
                  value={newSchool.school_type}
                  onChange={(e) =>
                    setNewSchool({ ...newSchool, school_type: e.target.value })
                  }
                >
                  <option value="basic">Basic</option>
                  <option value="secondary">Secondary</option>
                  <option value="tertiary">Tertiary</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSchoolModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateSchool}
              >
                Create School
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Account Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateUserModal(false); setCreatedCredentials(null); setBulkResult(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Create User Account</h2>
              <button className="close-btn" onClick={() => { setShowCreateUserModal(false); setCreatedCredentials(null); setBulkResult(null); }}>
                <X size={24} />
              </button>
            </div>

            {/* Tab switcher */}
            {!createdCredentials && !bulkResult && (
              <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 24px' }}>
                {['single', 'csv'].map(tab => (
                  <button key={tab} onClick={() => setCreateUserTab(tab)} style={{
                    padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: 14,
                    color: createUserTab === tab ? '#2563eb' : '#6b7280',
                    borderBottom: createUserTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                    marginBottom: -1
                  }}>
                    {tab === 'single' ? 'Single Account' : 'CSV Bulk Import'}
                  </button>
                ))}
              </div>
            )}

            {createdCredentials ? (
              <div className="modal-content">
                <div style={{ padding: '16px 0', textAlign: 'center' }}>
                  <div style={{ fontSize: 14, color: '#555', marginBottom: 20 }}>
                    Share these credentials securely. The user must change their password on first login.
                  </div>
                  <div style={{ background: '#f0f4f0', borderRadius: 10, padding: '20px 24px', textAlign: 'left', fontFamily: 'monospace', fontSize: 14, marginBottom: 20 }}>
                    <div><strong>Name:</strong> {createdCredentials.name}</div>
                    <div style={{ marginTop: 8 }}><strong>Email:</strong> {createdCredentials.email}</div>
                    <div style={{ marginTop: 8 }}><strong>Password:</strong> <span style={{ background: '#e0e8e0', padding: '2px 8px', borderRadius: 4 }}>{createdCredentials.password}</span></div>
                  </div>
                  <p style={{ fontSize: 12, color: '#888' }}>This password will not be shown again.</p>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setCreatedCredentials(null)}>Create Another</button>
                  <button className="btn btn-primary" onClick={() => { setShowCreateUserModal(false); setCreatedCredentials(null); }}>Done</button>
                </div>
              </div>
            ) : bulkResult ? (
              <div className="modal-content">
                <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{bulkResult.created} created</div>
                  {bulkResult.skipped > 0 && <div style={{ fontSize: 14, color: '#dc2626', marginTop: 4 }}>{bulkResult.skipped} skipped</div>}
                </div>
                {bulkResult.accounts?.length > 0 && (
                  <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead><tr style={{ background: '#f3f4f6' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Email</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Password</th>
                      </tr></thead>
                      <tbody>{bulkResult.accounts.map((a, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '6px 10px' }}>{a.name}</td>
                          <td style={{ padding: '6px 10px' }}>{a.email}</td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{a.generated_password}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <details style={{ fontSize: 12, color: '#dc2626', marginBottom: 16 }}>
                    <summary style={{ cursor: 'pointer' }}>{bulkResult.errors.length} skipped rows</summary>
                    {bulkResult.errors.map((e, i) => <div key={i} style={{ padding: '2px 0' }}>Row {e.row}: {e.reason} ({e.email})</div>)}
                  </details>
                )}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setBulkResult(null)}>Import Another</button>
                  <button className="btn btn-primary" onClick={() => { setShowCreateUserModal(false); setBulkResult(null); }}>Done</button>
                </div>
              </div>
            ) : createUserTab === 'single' ? (
              <div className="modal-content">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={newUserForm.first_name} onChange={e => setNewUserForm({ ...newUserForm, first_name: e.target.value })} placeholder="First name" />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" value={newUserForm.last_name} onChange={e => setNewUserForm({ ...newUserForm, last_name: e.target.value })} placeholder="Last name" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={newUserForm.email} onChange={e => setNewUserForm({ ...newUserForm, email: e.target.value })} placeholder="user@school.edu" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="text" value={newUserForm.phone} onChange={e => setNewUserForm({ ...newUserForm, phone: e.target.value })} placeholder="+233 ..." />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={newUserForm.role} onChange={e => setNewUserForm({ ...newUserForm, role: e.target.value })}>
                    <option value="school_admin">School Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="security_officer">Security Officer</option>
                    <option value="driver">Driver</option>
                    <option value="hr">HR</option>
                    <option value="parent">Parent</option>
                    <option value="student">Student</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>School</label>
                  <select value={newUserForm.school_id} onChange={e => setNewUserForm({ ...newUserForm, school_id: e.target.value, campus_id: '' })}>
                    <option value="">— Select school —</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                {newUserForm.school_id && newUserCampuses.length > 0 && (
                  <div className="form-group">
                    <label>Campus</label>
                    <select value={newUserForm.campus_id} onChange={e => setNewUserForm({ ...newUserForm, campus_id: e.target.value })}>
                      <option value="">— Whole school (no campus restriction) —</option>
                      {newUserCampuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Restricting to a campus limits this account to that campus's students, staff, classes, and fees.
                    </p>
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowCreateUserModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleCreateUser}>Create Account</button>
                </div>
              </div>
            ) : (
              <div className="modal-content">
                <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                  Upload a CSV with columns: <strong>email, first_name, last_name, role</strong> (+ optional: phone, school_id).
                  All created accounts will require a password change on first login.
                </p>
                <button className="btn btn-secondary" style={{ marginBottom: 16 }} onClick={downloadTemplate}>
                  Download Template
                </button>
                <div className="form-group">
                  <label>CSV File</label>
                  <input type="file" accept=".csv" onChange={e => setBulkFile(e.target.files[0] || null)} />
                  {bulkFile && <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{bulkFile.name}</div>}
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowCreateUserModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleBulkUpload} disabled={bulkLoading || !bulkFile}>
                    {bulkLoading ? 'Importing…' : 'Import Accounts'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );

  return <Layout>{pageContent}</Layout>;
}

export default SuperAdminPortalPage;
